/**
 * V1.2 Block 4 — Freigabe-Dialog (per row in "Meine Dateien", folders AND
 * files). A dialog, not a route: the path is the place, the dialog a transient
 * step (consistent with "#7 lives in the file view of #3").
 *
 * Shows the effective principal list (inherited entries marked, managed at
 * their source), adds people via Freigabe-ID (Block 3 — no search, no
 * autocomplete, no e-mail resolution), changes roles, revokes. Role LABELS are
 * Mitbesitzer · Bearbeiten · Lesen; the DB values owner|editor|viewer are
 * untouched. Only owner/admin reach this dialog (manage_acl via the guard —
 * the API answers 403 otherwise; the row button is disabled without rights).
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError, ryfeApi } from "../api";

import "./ryfe.scss";

import type { AclEntry, PendingInvite } from "../api";

const ROLE_LABELS: Record<string, string> = {
  owner: "Mitbesitzer",
  editor: "Bearbeiten",
  viewer: "Lesen",
};

/**
 * Revision R3 — der INHALT des Freigabe-Dialogs als wiederverwendbares Panel:
 * derselbe Baustein dient dem Dialog in „Meine Dateien" UND dem neuen
 * „Zugriff"-Tab des Teilen-Submenüs, damit gesetzte Freigaben an JEDEM
 * Dokument sichtbar sind (ein Guard, eine Oberfläche — kein Duplikat).
 */
export const ShareAccessPanel = ({
  wsId,
  target,
}: {
  wsId: string;
  target: { kind: "folder" | "file"; id: string; name: string };
}) => {
  const [entries, setEntries] = useState<AclEntry[] | null>(null);
  const [pending, setPending] = useState<PendingInvite[]>([]);
  const [shareId, setShareId] = useState("");
  const [addRole, setAddRole] = useState("viewer");
  // R4 (Punkt 8.2) — Einladung per E-Mail, direkt in der Freigabeeinstellung.
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"viewer" | "editor">("viewer");
  const [inviteOk, setInviteOk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const isFile = target.kind === "file";

  const load = useCallback(async () => {
    try {
      const r = isFile
        ? await ryfeApi.listFileAcl(wsId, target.id)
        : await ryfeApi.listFolderAcl(wsId, target.id);
      setEntries(r.entries);
      setPending(r.pending || []);
      setForbidden(false);
      return;
    } catch (e) {
      // Ohne manage_acl (z. B. als Empfänger einer Freigabe) antwortet der
      // Guard 403 — dann informiert das Panel statt einen Fehler zu werfen.
      if (e instanceof ApiError && e.status === 403) {
        setForbidden(true);
        setEntries([]);
        return;
      }
      setError(e instanceof ApiError ? e.message : "Laden fehlgeschlagen");
    }
  }, [wsId, target.id, isFile]);

  useEffect(() => {
    load();
  }, [load]);

  const setAcl = (principalId: string, role: string) =>
    isFile
      ? ryfeApi.setFileAcl(wsId, target.id, {
          principalType: "user",
          principalId,
          role,
        })
      : ryfeApi.setFolderAcl(wsId, target.id, {
          principalType: "user",
          principalId,
          role,
        });

  const add = async () => {
    setError(null);
    const id = shareId.trim();
    if (!id) {
      return;
    }
    setBusy(true);
    try {
      // Knowing the Freigabe-ID is the introduction — resolve confirms by name.
      const hit = await ryfeApi.resolveShareId(id);
      await setAcl(hit.userId, addRole);
      setShareId("");
      await load();
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 404
          ? "Keine Person mit dieser Freigabe-ID."
          : e instanceof ApiError
          ? e.message
          : "Hinzufügen fehlgeschlagen",
      );
    } finally {
      setBusy(false);
    }
  };

  // R4 (Punkt 8.2) — Einladung per E-Mail: existenz-neutral (bestehendes Konto
  // → sofortiger ACL-Eintrag; sonst sichtbare, widerrufbare „ausstehend"-Zeile).
  const invite = async () => {
    setError(null);
    setInviteOk(false);
    const email = inviteEmail.trim();
    if (!email || !email.includes("@")) {
      setError("Bitte eine gültige E-Mail-Adresse angeben.");
      return;
    }
    setBusy(true);
    try {
      if (isFile) {
        await ryfeApi.inviteToFile(wsId, target.id, email, inviteRole);
      } else {
        await ryfeApi.inviteToFolder(wsId, target.id, email, inviteRole);
      }
      setInviteEmail("");
      setInviteOk(true);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Einladen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const revokeInvite = async (p: PendingInvite) => {
    setError(null);
    try {
      if (isFile) {
        await ryfeApi.revokeInvite(wsId, target.id, p.email);
      } else {
        await ryfeApi.revokeFolderInvite(wsId, target.id, p.email);
      }
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Widerruf fehlgeschlagen");
    }
  };

  const changeRole = async (entry: AclEntry, role: string) => {
    setError(null);
    try {
      await setAcl(entry.principalId, role);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Ändern fehlgeschlagen");
    }
  };

  const revoke = async (entry: AclEntry) => {
    setError(null);
    try {
      if (isFile) {
        await ryfeApi.removeFileAcl(wsId, target.id, {
          principalType: entry.principalType,
          principalId: entry.principalId,
        });
      } else {
        await ryfeApi.removeFolderAcl(wsId, target.id, {
          principalType: entry.principalType,
          principalId: entry.principalId,
        });
      }
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Entzug fehlgeschlagen");
    }
  };

  if (forbidden) {
    return (
      <div className="ryfe-muted" style={{ padding: 0 }}>
        Die Freigaben dieses Objekts kann nur verwalten, wer Besitzer-Rechte
        daran hat.
      </div>
    );
  }

  return (
    <>
      {!entries ? (
        <div className="ryfe-muted">Laden…</div>
      ) : entries.length === 0 && pending.length === 0 ? (
        <div className="ryfe-muted">Noch keine Freigaben.</div>
      ) : (
        <div className="ryfe-sharelist">
          {entries.map((e) => (
            <div className="ryfe-sharelist__row" key={e.id}>
              <span className="ryfe-sharelist__label">
                {e.principalType === "mcp" ? "🤖 " : ""}
                {e.label}
                {e.inherited && (
                  <span
                    className="ryfe-badge ryfe-badge--warn"
                    title={`Geerbt${
                      e.via ? ` von „${e.via}"` : ""
                    } — dort verwalten`}
                  >
                    geerbt{e.via ? ` · ${e.via}` : ""}
                  </span>
                )}
              </span>
              {e.inherited || e.principalType !== "user" ? (
                <span className="ryfe-muted" style={{ padding: 0 }}>
                  {ROLE_LABELS[e.role] || e.role}
                  {e.principalType === "mcp" && " · via MCP-Token-Seite"}
                </span>
              ) : (
                <>
                  <select
                    className="ryfe-select"
                    value={e.role}
                    onChange={(ev) => changeRole(e, ev.target.value)}
                    aria-label={`Rolle für ${e.label}`}
                  >
                    <option value="owner">{ROLE_LABELS.owner}</option>
                    <option value="editor">{ROLE_LABELS.editor}</option>
                    <option value="viewer">{ROLE_LABELS.viewer}</option>
                  </select>
                  <button
                    className="ryfe-chip"
                    onClick={() => revoke(e)}
                    title="Freigabe entziehen"
                  >
                    Entziehen
                  </button>
                </>
              )}
            </div>
          ))}
          {/* R4 (Punkt 8.2) — ausstehende E-Mail-Einladungen: IMMER sichtbar,
              auch ohne Konto dahinter, damit sie widerrufbar sind. */}
          {pending.map((p) => (
            <div className="ryfe-sharelist__row" key={p.id}>
              <span className="ryfe-sharelist__label">
                ✉️ {p.email}
                <span
                  className="ryfe-badge ryfe-badge--warn"
                  title="Eingeladen — noch kein Konto. Wird beim Registrieren der Adresse automatisch aktiv."
                >
                  eingeladen
                </span>
              </span>
              <span className="ryfe-muted" style={{ padding: 0 }}>
                {ROLE_LABELS[p.role] || p.role}
              </span>
              <button
                className="ryfe-chip"
                onClick={() => revokeInvite(p)}
                title="Einladung widerrufen"
              >
                Widerrufen
              </button>
            </div>
          ))}
        </div>
      )}

      {/* v1.6 — Einladen per E-Mail für Datei UND Ordner (vorher nur Datei). */}
      <div className="ryfe-sharelist__add">
        <input
          className="ryfe-field-input"
          type="email"
          placeholder="Per E-Mail einladen, z. B. name@firma.de"
          value={inviteEmail}
          onChange={(e) => {
            setInviteEmail(e.target.value);
            setInviteOk(false);
          }}
          aria-label="E-Mail-Adresse"
        />
        <select
          className="ryfe-select"
          value={inviteRole}
          onChange={(e) => setInviteRole(e.target.value as "viewer" | "editor")}
          aria-label="Rolle für Einladung"
        >
          <option value="viewer">{ROLE_LABELS.viewer}</option>
          <option value="editor">{ROLE_LABELS.editor}</option>
        </select>
        <button
          className="ryfe-btn ryfe-btn--primary"
          onClick={invite}
          disabled={busy || !inviteEmail.trim()}
        >
          Einladen
        </button>
      </div>
      {inviteOk && (
        <p className="ryfe-muted" style={{ padding: 0, fontSize: "0.82rem" }}>
          Einladung gespeichert — sie erscheint oben in der Liste.
        </p>
      )}

      <div className="ryfe-sharelist__add">
        <input
          className="ryfe-field-input"
          placeholder="Freigabe-ID, z. B. 7f3a-9c21-4d80"
          value={shareId}
          onChange={(e) => setShareId(e.target.value)}
          aria-label="Freigabe-ID"
        />
        <select
          className="ryfe-select"
          value={addRole}
          onChange={(e) => setAddRole(e.target.value)}
          aria-label="Rolle"
        >
          <option value="viewer">{ROLE_LABELS.viewer}</option>
          <option value="editor">{ROLE_LABELS.editor}</option>
          <option value="owner">{ROLE_LABELS.owner}</option>
        </select>
        <button
          className="ryfe-btn ryfe-btn--primary"
          onClick={add}
          disabled={busy || !shareId.trim()}
        >
          Hinzufügen
        </button>
      </div>

      {error && <div className="ryfe-auth__error">{error}</div>}

      <p className="ryfe-muted" style={{ padding: 0, fontSize: "0.82rem" }}>
        Per Link geteilte Schnappschüsse sind unabhängig von diesen Rechten und
        werden hier nicht verwaltet.
      </p>
    </>
  );
};

export const ShareSettingsDialog = ({
  wsId,
  target,
  onClose,
}: {
  wsId: string;
  target: { kind: "folder" | "file"; id: string; name: string };
  onClose: () => void;
}) => (
  <div className="ryfe-modal" role="dialog" aria-modal="true" onClick={onClose}>
    <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
      <h2 className="ryfe-panel__title">Freigabeeinstellung · {target.name}</h2>
      <ShareAccessPanel wsId={wsId} target={target} />
      <div className="ryfe-modal__actions">
        <span className="ryfe-topbar__spacer" />
        <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
          Schließen
        </button>
      </div>
    </div>
  </div>
);
